/* eslint-disable react/no-unescaped-entities */
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { User, Save, Lock, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profilo() {
  const { user, fetchUser } = useAuth();
  const [form, setForm] = useState({
    nome: user?.nome || '',
    cognome: user?.cognome || '',
    telefono: user?.telefono || '',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/users/profile`, form);
      await fetchUser();
      toast.success('Profilo aggiornato con successo');
    } catch (err) {
      toast.error('Errore nell\'aggiornamento del profilo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="py-10 sm:py-14">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-[#1F2A37] mb-8" style={{fontFamily:'Space Grotesk'}}>Il Mio Profilo</h1>

        <Card className="shadow-[var(--shadow-md)]">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#238A92] flex items-center justify-center text-white">
                <User size={24} />
              </div>
              <div>
                <CardTitle style={{fontFamily:'Space Grotesk'}}>{user?.nome} {user?.cognome}</CardTitle>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome</Label>
                  <Input id="nome" name="nome" value={form.nome} onChange={handleChange} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cognome">Cognome</Label>
                  <Input id="cognome" name="cognome" value={form.cognome} onChange={handleChange} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefono">Telefono</Label>
                <Input id="telefono" name="telefono" value={form.telefono} onChange={handleChange} placeholder="+39 333 1234567" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={user?.email || ''} disabled className="bg-muted" />
                <p className="text-xs text-muted-foreground">L'email non puo essere modificata</p>
              </div>
              <Button type="submit" disabled={saving} className="bg-[#238A92] hover:bg-[#0F5E66] text-white">
                <Save size={14} className="mr-1" /> {saving ? 'Salvataggio...' : 'Salva Modifiche'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Cambio Password */}
        <ChangePasswordCard />
      </div>
    </div>
  );
}

function ChangePasswordCard() {
  const [pwForm, setPwForm] = useState({ current: '', new: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const handleChangePw = async (e) => {
    e.preventDefault();
    setPwError('');
    if (pwForm.new.length < 6) { setPwError('La nuova password deve avere almeno 6 caratteri'); return; }
    if (pwForm.new !== pwForm.confirm) { setPwError('Le password non coincidono'); return; }
    
    setPwSaving(true);
    try {
      await axios.put(`${API}/users/change-password?current_password=${encodeURIComponent(pwForm.current)}&new_password=${encodeURIComponent(pwForm.new)}`);
      toast.success('Password aggiornata con successo');
      setPwForm({ current: '', new: '', confirm: '' });
    } catch (err) {
      setPwError(err.response?.data?.detail || 'Errore nel cambio password');
    } finally { setPwSaving(false); }
  };

  return (
    <Card className="shadow-[var(--shadow-md)] mt-6">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <Lock size={20} className="text-amber-600" />
          </div>
          <CardTitle style={{fontFamily:'Space Grotesk'}}>Cambia Password</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleChangePw} className="space-y-4">
          {pwError && <Alert variant="destructive"><AlertCircle size={16} /><AlertDescription>{pwError}</AlertDescription></Alert>}
          <div className="space-y-2">
            <Label>Password attuale</Label>
            <Input type="password" value={pwForm.current} onChange={e => setPwForm(p => ({...p, current: e.target.value}))} required />
          </div>
          <div className="space-y-2">
            <Label>Nuova password</Label>
            <Input type="password" value={pwForm.new} onChange={e => setPwForm(p => ({...p, new: e.target.value}))} required placeholder="Minimo 6 caratteri" />
          </div>
          <div className="space-y-2">
            <Label>Conferma nuova password</Label>
            <Input type="password" value={pwForm.confirm} onChange={e => setPwForm(p => ({...p, confirm: e.target.value}))} required />
          </div>
          <Button type="submit" disabled={pwSaving} variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50">
            <Lock size={14} className="mr-1" /> {pwSaving ? 'Aggiornamento...' : 'Cambia Password'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
